import { useEffect, useState } from 'react';
import type { IPatient } from '@common/interfaces/patient';
import type { IHolterDesktopProcess } from '@frontend/components/interfaces';
import {
    DesktopProcessActions,
    DesktopProcessErrorCode,
    HardwareProcessStatus,
    RecorderErrorCodes,
} from '@common/enums';
import { getPatient } from '@frontend/services/api/patientsApiService';
import {
    getOneProcessActivity,
    getProcessActivityCustomControlData,
} from '@frontend/services/api/processActivitiesApiService';
import { getRequest, getRequestProcedure } from '@frontend/services/api/requestApiService';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { HolterContainer } from './HolterContainer';
import { HolterCurrent } from './HolterCurrent';
import { HolterInformation } from './HolterInformation';
import { PatientInformation } from './HolterPatientInformation';

type HolterDashBoardCurrentHolterProps = {
    cancelButton?: JSX.Element;
    clearButton?: JSX.Element;
    desktopProcess?: IHolterDesktopProcess;
    disableTimer: number;
    hasDesktopProcessTimedOut: boolean;
    noKsyosHandler: boolean;
    readButton?: JSX.Element;
};

export function HolterDashBoardCurrentHolter(props: Readonly<HolterDashBoardCurrentHolterProps>) {
    const abortSignal = useAbortSignal();
    const [patient, setPatient] = useState<IPatient | null>(null);
    const [procedureType, setProcedureType] = useState<string>();
    const [configurationDatetime, setConfigurationDatetime] = useState<string>();

    useEffect(() => {
        if (props.desktopProcess?.processActivityId) {
            loadProcessActivityAndPatient(props.desktopProcess.processActivityId);
        }
    }, [props.desktopProcess?.processActivityId]);

    useEffect(() => {
        if (props.desktopProcess?.requestId) {
            loadProcedureAndPatient(props.desktopProcess.requestId);
        }
    }, [props.desktopProcess?.requestId]);

    if (props.noKsyosHandler || props.hasDesktopProcessTimedOut) {
        return (
            <HolterContainer>
                <HolterCurrent
                    appearance="warning"
                    hasDesktopProcessTimedOut={props.hasDesktopProcessTimedOut}
                    noKsyosHandler={props.noKsyosHandler}
                />
            </HolterContainer>
        );
    }

    if (!props.desktopProcess) {
        return (
            <HolterContainer>
                <HolterCurrent appearance="info" />
            </HolterContainer>
        );
    }

    if (props.desktopProcess.data?.error) {
        return (
            <HolterContainer>
                <HolterCurrent
                    appearance="warning"
                    text={getHolterDesktopErrorText(props.desktopProcess.data.error)}
                />
            </HolterContainer>
        );
    }

    if (props.desktopProcess.action === DesktopProcessActions.detectHolter) {
        if (!props.desktopProcess.data) {
            return (
                <HolterContainer>
                    <HolterCurrent
                        appearance="info"
                        isLoading={true}
                        text="Even geduld, we  controleren of er een recorder is aangesloten"
                    />
                </HolterContainer>
            );
        }

        if (
            props.desktopProcess.data.holterPresent === undefined ||
            props.desktopProcess.data.holterPresent === false
        ) {
            return (
                <HolterContainer>
                    <HolterCurrent
                        appearance="warning"
                        text="We kunnen geen aangesloten recorder vinden. Sluit een recorder aan of controleer de verbinding."
                    />
                </HolterContainer>
            );
        }

        if (
            props.desktopProcess.data.isProcessCancelled === true ||
            (!props.desktopProcess.requestId && !props.desktopProcess.processActivityId)
        ) {
            if (
                props.desktopProcess.data.recordingsAvailable === true ||
                props.desktopProcess.data.isProcessCancelled === true
            ) {
                return (
                    <HolterContainer>
                        <HolterCurrent
                            appearance="warning"
                            clearButton={props.clearButton}
                            disableTimer={props.disableTimer}
                            isHalfWidth={true}
                            text="We kunnen de gegevens op deze recorder niet uitlezen. Om hem voor een nieuw onderzoek in te stellen, moet hij eerst worden leeggemaakt."
                        />
                        <PatientInformation
                            patient={patient}
                            processDesktopData={props.desktopProcess?.data}
                        />
                    </HolterContainer>
                );
            } else {
                return (
                    <HolterContainer>
                        <HolterCurrent
                            appearance="success"
                            isHalfWidth={true}
                            text="De recorder is gereed om in te stellen voor een nieuw onderzoek"
                        />
                        {props.desktopProcess?.data?.holterSerialNumber && (
                            <HolterInformation
                                batteryChargePercentage={props.desktopProcess.data.batteryChargePercentage}
                                firmwareVersion={props.desktopProcess.data.firmwareVersion}
                                recorderId={props.desktopProcess.data.holterSerialNumber}
                            />
                        )}
                    </HolterContainer>
                );
            }
        }

        if (
            props.desktopProcess.data.recordingsAvailable === undefined ||
            props.desktopProcess.data.recordingsAvailable === false
        ) {
            return (
                <HolterContainer>
                    <HolterCurrent
                        appearance="success"
                        cancelButton={props.cancelButton}
                        disableTimer={props.disableTimer}
                        isHalfWidth={true}
                        text="De recorder is ingesteld. Je kunt deze meegeven aan de patiënt."
                    />
                    <PatientInformation
                        configurationDatetime={configurationDatetime}
                        patient={patient}
                        procedureType={procedureType}
                        processDesktopData={props.desktopProcess?.data}
                    />
                </HolterContainer>
            );
        }

        if (props.desktopProcess.data.recordingsAvailable && props.desktopProcess.data.recordingsAvailable === true) {
            return (
                <HolterContainer>
                    <HolterCurrent
                        appearance="success"
                        cancelButton={props.desktopProcess.data.canClearRecordings ? props.cancelButton : undefined}
                        disableTimer={props.disableTimer}
                        isHalfWidth={true}
                        readButton={props.readButton}
                        text="Er is een recorder met een ECG opname gevonden. Je kunt beginnen met uitlezen."
                    />
                    <PatientInformation
                        configurationDatetime={configurationDatetime}
                        patient={patient}
                        procedureType={procedureType}
                        processDesktopData={props.desktopProcess?.data}
                    />
                </HolterContainer>
            );
        }
    }

    if (props.desktopProcess.action === DesktopProcessActions.readHolter && props.desktopProcess.data) {
        if (props.desktopProcess.data.downloadProgress === undefined) {
            return (
                <HolterContainer>
                    <HolterCurrent
                        appearance="info"
                        isHalfWidth={true}
                        isLoading={true}
                        text="De recorder wordt uitgelezen. Koppel de recorder niet los."
                    />
                    <PatientInformation
                        configurationDatetime={configurationDatetime}
                        patient={patient}
                        procedureType={procedureType}
                        processDesktopData={props.desktopProcess?.data}
                    />
                </HolterContainer>
            );
        }
        if (props.desktopProcess.data.downloadProgress == 100) {
            return (
                <HolterContainer>
                    <HolterCurrent
                        appearance="success"
                        isHalfWidth={true}
                        text="ECG opname is succesvol uitgelezen. Het bestand wordt nu geüpload naar ons platform, zet de computer nog niet uit. Je kunt de voortgang hieronder volgen in de lijst met ‘Actieve opnames’."
                    />
                    <PatientInformation
                        configurationDatetime={configurationDatetime}
                        patient={patient}
                        procedureType={procedureType}
                        processDesktopData={props.desktopProcess?.data}
                    />
                </HolterContainer>
            );
        }
        if (props.desktopProcess.data.downloadProgress !== undefined) {
            return (
                <HolterContainer>
                    <HolterCurrent
                        appearance="info"
                        isHalfWidth={true}
                        isLoading={true}
                        text="De recorder wordt uitgelezen. Koppel de recorder niet los."
                        uploadPercentage={props.desktopProcess.data.downloadProgress}
                    />
                    <PatientInformation
                        configurationDatetime={configurationDatetime}
                        patient={patient}
                        procedureType={procedureType}
                        processDesktopData={props.desktopProcess?.data}
                    />
                </HolterContainer>
            );
        }
    }

    if (props.desktopProcess.action === DesktopProcessActions.clearHolter) {
        if (props.desktopProcess.status === HardwareProcessStatus.completed) {
            return (
                <HolterContainer>
                    <HolterCurrent
                        appearance="success"
                        text="De recorder is geleegd en is gereed om in te stellen voor een nieuw onderzoek"
                    />
                </HolterContainer>
            );
        } else if (
            props.desktopProcess.status === HardwareProcessStatus.starting ||
            props.desktopProcess.status === HardwareProcessStatus.running
        ) {
            return (
                <HolterContainer>
                    <HolterCurrent
                        appearance="info"
                        text="De recorder wordt geleegd..."
                    />
                </HolterContainer>
            );
        }
    }

    return null;

    async function loadProcedureAndPatient(requestId: string) {
        const procedureResult = await getRequestProcedure(requestId, abortSignal);
        const procedure = procedureResult;
        let procedureTypeResult = procedure?.customControlData.procedureType;

        const requestResult = await getRequest(requestId, abortSignal);
        const request = requestResult;

        if (request && !request.isDirectProcedureAdded) {
            procedureTypeResult = request.customControlData.procedureType;
        }
        setProcedureType(procedureTypeResult);
        setConfigurationDatetime(procedure?.customControlData.configurationDatetime);
        const patientId = procedureResult.patientId;

        const patientResult = await getPatient(patientId, undefined, abortSignal);
        setPatient(patientResult);
    }

    async function loadProcessActivityAndPatient(processActivityId: string) {
        const customControlData = await getProcessActivityCustomControlData(processActivityId, abortSignal);
        setProcedureType(customControlData?.type_onderzoek);
        setConfigurationDatetime(customControlData?.configurationDatetime);

        const processActivity = await getOneProcessActivity(processActivityId, abortSignal);

        const patientResult = await getPatient(processActivity.process.patientId, undefined, abortSignal);
        setPatient(patientResult);
    }

    function getHolterDesktopErrorText(error: string) {
        switch (error) {
            case DesktopProcessErrorCode.HOLTER_MUST_BE_PLUGGED_IN_TO_READ_RESULTS:
                return 'We kunnen geen aangesloten recorder vinden. Sluit een recorder aan of controleer de verbinding.';
            case DesktopProcessErrorCode.CANNOT_CLEAR_HOLTER_WITH_RECORDINGS:
                return 'Je kunt een ingestelde recorder die al een opname heeft gemaakt niet legen, maar alleen uitlezen';
            case DesktopProcessErrorCode.MULTIPLE_RECORDERS_ATTACHED:
                return 'We zien dat er meer dan 1 recorder is aangesloten aan je computer. Sluit maximaal 1 recorder aan en probeer het opnieuw.';
            case RecorderErrorCodes.IDENTIFYING_HOLTER_FAILED:
                return 'Om de recorder uit te lezen, moet je ingelogd zijn in de praktijk waarin deze is ingesteld. Het lijkt erop dat je niet in de juiste praktijk bent ingelogd. Wissel van praktijk via het gebruikersmenu rechtsboven.';
            default:
                return 'Sorry, er lijkt er iets mis te zijn met de recorder die is aangesloten. Neem contact op met Servicedesk via 020 600 00 60, dan zoeken we het voor je uit.';
        }
    }
}
